import { MEMBERSHIP_SUPPORT_GROUP_ID } from '@provision/conversation-constants';
import {
  ChannelIdentifiers,
  ConversationChannelType,
  ConversationChatApp,
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ActionType, Conversation, MacroActionRule } from 'src/graphql';

import {
  SubmitConversationDataChannel,
  SubmitConversationDataParticipant,
} from 'src/graphql/entities/conversation/conversation.types';
import {
  ParticipantChannelInfo,
  ParticipantSelectorItem,
  SelectedParticipants,
} from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { isSameParticipant } from 'src/pages/Conversations/Chat/utils';
import { getChatIdentifier } from 'src/utils/conversation';

interface GetPreparedParticipantsInput {
  selectedParticipants: SelectedParticipants;
  conversation: Conversation;
  userId: string;
  userPartnerId: number;
}

export function validateMacroActions(actions: MacroActionRule[]) {
  const repeatingLabels = [] as string[];
  return actions.map((item, i) => {
    if ('type' in item && item.type === ActionType.ManageConversationLabels) {
      const isFirstLabelActionOfType =
        actions.findIndex(
          (action) => 'operation' in action && action.operation === item.operation,
        ) === i;

      const labelIds = item.labelIds || [];
      const hasRepeatingLabels = labelIds.some((labelId) => repeatingLabels.includes(labelId));
      repeatingLabels.push(...labelIds);

      return {
        ...item,
        isValid: isFirstLabelActionOfType && !hasRepeatingLabels,
      };
    }

    if ('type' in item && item.type === ActionType.Signal) {
      const isFirstSignalAction =
        actions.findIndex(
          (action) => 'type' in action && action.type === ActionType.Signal,
        ) === i;

      return {
        ...item,
        isValid: isFirstSignalAction,
      };
    }

    const isFirstDataActionOfType =
      actions.findIndex(
        (action) =>
          'attribute' in action && 'attribute' in item && action.attribute === item.attribute,
      ) === i;
    return {
      ...item,
      isValid: isFirstDataActionOfType,
    };
  });
}

export function getPreparedParticipants(
  input: GetPreparedParticipantsInput,
): SubmitConversationDataParticipant[] {
  const result: SubmitConversationDataParticipant[] = [];

  input.selectedParticipants.items.filter(Boolean).forEach((participant) => {
    const isRequester =
      input.selectedParticipants.to &&
      isSameParticipant(participant, input.selectedParticipants.to);

    result.push({
      entity: participant.participantEntity,
      identity: participant.participantIdentity,
      channels: getPreparedChannels(participant, input.conversation),
      isRequester,
      entityPartnerId: getPreparedEntityPartnerIdValue(
        input.conversation.partnerId,
        participant.entityPartnerId,
      ),
      name: isRequester ? input.selectedParticipants.to!.name : undefined,
    });
  });

  const sender = findSenderParticipant(result, input.userId);
  if (sender) {
    prepareSenderChannel(sender, input.userId);
    return result;
  }
  result.push(
    getSenderParticipant(
      input.userId,
      input.userPartnerId !== input.conversation.partnerId ? input.userPartnerId : undefined,
    ),
  );
  return result;
}

function getPreparedEntityPartnerIdValue(
  conversationPartnerId: number,
  entityPartnerId?: number,
): number | undefined {
  if (!entityPartnerId || entityPartnerId === conversationPartnerId) return undefined;
  return entityPartnerId;
}

export function getActiveChannels(
  participants: SubmitConversationDataParticipant[],
): ChannelIdentifiers[] {
  return participants.flatMap((participant) => {
    return participant.channels.map(({ externalIdentifier, internalIdentifier }) => ({
      externalIdentifier,
      internalIdentifier,
    }));
  });
}

export function hasEmailChannel(participant?: ParticipantSelectorItem): boolean {
  return Boolean(
    participant?.channels.some(
      (channel) =>
        (channel.conversationChannelTypeId as ConversationChannelType) ===
        ConversationChannelType.Email,
    ),
  );
}

function findSenderParticipant(
  participants: SubmitConversationDataParticipant[],
  userId: string,
): SubmitConversationDataParticipant | undefined {
  return participants.find(
    (channel) =>
      channel.entity === ConversationParticipantEntity.PlatformUser &&
      channel.identity === userId,
  );
}

function prepareSenderChannel(
  sender: SubmitConversationDataParticipant,
  userId: string,
): void {
  const senderChannel = sender.channels.find(
    (channel) => channel.channelType === ConversationChannelType.Chat,
  );
  if (senderChannel) {
    senderChannel.useToSendMessage = true;
    return;
  }
  sender.channels.push(getSenderChannel(userId));
}

export function getSenderParticipant(
  userId: string,
  entityPartnerId?: number,
): SubmitConversationDataParticipant {
  return {
    entity: ConversationParticipantEntity.PlatformUser,
    identity: userId,
    channels: [getSenderChannel(userId)],
    entityPartnerId,
  };
}

function getSenderChannel(userId: string): SubmitConversationDataChannel {
  return {
    channelType: ConversationChannelType.Chat,
    internalIdentifier: ConversationChatApp.Portal,
    externalIdentifier: getChatExternalIdentifier({
      participantEntity: ConversationParticipantEntity.PlatformUser,
      participantIdentity: userId,
    }),
    useToSendMessage: true,
    participationMode: ConversationParticipationMode.Assist,
  };
}

function getPreparedChannels(
  participantItem: ParticipantSelectorItem,
  conversation: Conversation,
): SubmitConversationDataChannel[] {
  const result: SubmitConversationDataChannel[] = [];

  participantItem.channels.filter(Boolean).forEach((channel) => {
    result.push({
      channelType: channel.conversationChannelTypeId as ConversationChannelType,
      internalIdentifier: getInternalEmail(conversation),
      externalIdentifier: channel.externalIdentifier,
      participationMode: ConversationParticipationMode.Engage,
    });
  });

  if (needToAddChatChannel(participantItem)) {
    result.push({
      channelType: ConversationChannelType.Chat,
      internalIdentifier: ConversationChatApp.Portal,
      externalIdentifier: getChatExternalIdentifier(participantItem),
    });
  }

  return result;
}

function getInternalEmail(conversation: Conversation): string {
  return conversation.conversationAssigneeGroupId === MEMBERSHIP_SUPPORT_GROUP_ID
    ? conversation.partner.brandGroups[0].membershipEmail
    : conversation.partner.brandGroups[0].supportEmail;
}

function needToAddChatChannel(participant: ParticipantSelectorItem): boolean {
  return Boolean(
    getChatExternalIdentifier(participant) && !channelsContainChat(participant.channels),
  );
}

function channelsContainChat(channels: ParticipantChannelInfo[]): boolean {
  return channels.some(
    (item) =>
      (item.conversationChannelTypeId as ConversationChannelType) ===
      ConversationChannelType.Chat,
  );
}

function getChatExternalIdentifier(
  participantInfo: Pick<ParticipantSelectorItem, 'participantEntity' | 'participantIdentity'>,
): string {
  switch (participantInfo.participantEntity) {
    case ConversationParticipantEntity.Client:
      return getChatIdentifier(
        ConversationChatApp.ClientGateway,
        participantInfo.participantIdentity,
      );
    case ConversationParticipantEntity.PlatformUser:
    case ConversationParticipantEntity.Bot:
      return getChatIdentifier(
        ConversationChatApp.Portal,
        participantInfo.participantIdentity,
      );
  }
  return '';
}
